import Skeleton from "@mui/material/Skeleton";
import React from "react";
import { NoData } from "./ChartMessages/NoData";
import { InsufficientInfoWrapper } from "./View/CompetitiveAnalysis/InsufficientInfoWrapper";
import { Market, SAMConfig, SOMConfig, Workspace } from "../../types/workspace";
import { DetectionSegment } from "./View/MarketLandscape/hooks/useProductDetectionsBySegment";
import { CompanyFilter } from "../../legacy/types/companyFilter";
import { FilterRange } from "../../components/filters/Ranges/types";
import { buildRangeLabel } from "../../components/filters/Ranges/utils";
import { isEqual } from "../../utils/jsUtils";

export type FilterConflictType =
  | "revenue"
  | "employee"
  | "geography"
  | "industry"
  | "products";
export interface FilterConflict {
  type: FilterConflictType;
  marketDefinition: string;
  filterValue: string;
  isOverridden: boolean;
}
interface ConflictCheck {
  type: string;
  marketDefinition: string;
  filterValue: string;
}

export function hasNoData(isLoading: boolean, chartData: unknown[]) {
  if (!isLoading) {
    return chartData.length === 0;
  }
  return false;
}

interface ChartContentParams {
  productIds?: unknown[];
  isLoading: boolean;
  noDataReturned: boolean;
  chartToRender: React.ReactNode;
  baseId: string;
}

export function determineChartContent({
  productIds,
  isLoading,
  noDataReturned,
  chartToRender,
  baseId,
}: ChartContentParams) {
  if (productIds && productIds.length === 0) {
    return <InsufficientInfoWrapper />;
  }

  if (isLoading) {
    return (
      <Skeleton
        id={`${baseId}-skeleton`}
        variant="rounded"
        height={350}
        sx={{ bgcolor: "rgba(52, 60, 67, 0.11)" }}
      />
    );
  }

  if (noDataReturned) {
    return <NoData />;
  }

  return chartToRender;
}

export const isRangeDefined = (range: FilterRange) => {
  if (range.min !== undefined && range.min !== null) return true;
  if (range.max !== undefined && range.max !== null) return true;
  return false;
};

export const isSomConfigured = (somConfig: SOMConfig): boolean => {
  const employeeRangeDefined = isRangeDefined(somConfig.employeeRange);
  const revenueRangeDefined = isRangeDefined(somConfig.revenueRange);

  return (
    employeeRangeDefined ||
    revenueRangeDefined ||
    somConfig.productIds.length > 0 ||
    somConfig.excludedProductIds.length > 0
  );
};

export const isSamConfigured = (samConfig: SAMConfig): boolean => {
  const arraysToCheck = [
    samConfig.industry.ids,
    samConfig.industry.sicIds,
    samConfig.industry.naicsIds,
    samConfig.industry.excludedIds,
    samConfig.industry.excludedSicIds,
    samConfig.industry.excludedNaicsIds,
    samConfig.geography.regionsIds,
    samConfig.geography.countryIds,
    samConfig.geography.stateIds,
    samConfig.geography.excludedCountryIds,
  ];
  return arraysToCheck.some((arr) => arr.length > 0);
};

export const determineInitialMarket = (
  configuration: Workspace["configuration"]
): Market | undefined => {
  if (!configuration) return undefined;

  const { som, sam } = configuration;

  if (isSomConfigured(som)) {
    return Market.SOM;
  }

  if (isSamConfigured(sam)) {
    return Market.SAM;
  }

  return Market.TAM;
};

export const generateChartSegmentNamesForCSV = (
  segmentValue: string
): string => {
  switch (segmentValue) {
    case DetectionSegment.COUNTRY:
      return "Country";
    case DetectionSegment.REGION:
      return "Region";
    case DetectionSegment.INDUSTRY:
      return "HG Industry";
    case DetectionSegment.INDUSTRY_NAICS:
      return "NAICS";
    case DetectionSegment.EMPLOYEE:
      return "Employee Range";
    case DetectionSegment.REVENUE:
      return "Revenue Range";
    default:
      return `${segmentValue}`;
  }
};

export const formatPenetrationPercentage = (value: number): string => {
  if (value < 1) {
    return "< 1";
  }
  return `${value}`;
};

/**
 * Checks if a value is valid for chart display (not NaN, Infinity, or negative)
 * @param value The value to check
 * @returns true if the value is valid for display
 */
export const isValidChartValue = (value: number): boolean => {
  return !Number.isNaN(value) && Number.isFinite(value) && value >= 0;
};

/**
 * Safely calculates percentage with validation
 * @param numerator The numerator value
 * @param denominator The denominator value
 * @returns Calculated percentage or 0 if invalid
 */
export const safeCalculatePercentage = (
  numerator: number,
  denominator: number
): number => {
  if (!denominator || denominator <= 0 || !Number.isFinite(denominator)) {
    return 0;
  }
  if (!Number.isFinite(numerator) || numerator < 0) {
    return 0;
  }
  const percentage = (numerator / denominator) * 100;
  return isValidChartValue(percentage) ? Number(percentage.toFixed(1)) : 0;
};

const createConflict = (check: ConflictCheck): FilterConflict => ({
  type: check.type as FilterConflictType,
  marketDefinition: check.marketDefinition,
  filterValue: check.filterValue,
  isOverridden: false,
});

export const getCurrentFilterTypes = (
  filters: CompanyFilter | undefined
): Set<FilterConflictType> => {
  const types = new Set<FilterConflictType>();
  if (!filters) return types;

  if (
    filters.countryAlpha2s?.length ||
    filters.regionNames?.length ||
    filters.stateIds?.length ||
    filters.excludedCountryAlpha2s?.length
  ) {
    types.add("geography");
  }
  if (
    filters.industry &&
    (filters.industry.industryIds?.length ||
      filters.industry.sicUS1987Ids?.length ||
      filters.industry.naics2012Ids?.length ||
      filters.industry.excludedIndustryIds?.length ||
      filters.industry.excludedSicUS1987Ids?.length ||
      filters.industry.excludedNaics2012Ids?.length)
  ) {
    types.add("industry");
  }
  if (filters.revenue || filters.hasFixedRevenue) {
    types.add("revenue");
  }
  if (filters.employees || filters.hasFixedEmployees) {
    types.add("employee");
  }
  if (filters.products?.length) {
    types.add("products");
  }

  return types;
};

export const hasMarketConfig = (
  workspace: Workspace | undefined,
  filterType: FilterConflictType
): boolean => {
  if (!workspace) return false;

  switch (filterType) {
    case "geography":
      return !!(
        workspace.configuration.sam.geography.countryIds?.length ||
        workspace.configuration.sam.geography.regionsIds?.length ||
        workspace.configuration.sam.geography.stateIds?.length ||
        workspace.configuration.sam.geography.excludedCountryIds?.length
      );

    case "industry":
      return !!(
        workspace.configuration.sam.industry.ids?.length ||
        workspace.configuration.sam.industry.sicIds?.length ||
        workspace.configuration.sam.industry.naicsIds?.length ||
        workspace.configuration.sam.industry.excludedIds?.length ||
        workspace.configuration.sam.industry.excludedSicIds?.length ||
        workspace.configuration.sam.industry.excludedNaicsIds?.length
      );

    case "products":
      return !!(
        workspace.configuration.som.productIds?.length ||
        workspace.configuration.som.excludedProductIds?.length
      );

    case "revenue":
      return isRangeDefined(workspace.configuration.som.revenueRange);

    case "employee":
      return isRangeDefined(workspace.configuration.som.employeeRange);

    default:
      return false;
  }
};

function isSamGeographyConfigured(geography: SAMConfig["geography"]): boolean {
  return !!(
    geography.countryIds?.length ||
    geography.regionsIds?.length ||
    geography.stateIds?.length ||
    geography.excludedCountryIds?.length
  );
}

function isSamIndustryConfigured(industry: SAMConfig["industry"]): boolean {
  return !!(
    industry.ids?.length ||
    industry.sicIds?.length ||
    industry.naicsIds?.length ||
    industry.excludedIds?.length ||
    industry.excludedSicIds?.length ||
    industry.excludedNaicsIds?.length
  );
}

function isSomProductsConfigured(
  productIds: number[],
  excludedProductIds: number[]
): boolean {
  return !!(productIds?.length || excludedProductIds?.length);
}

export function hasFilterConflict(
  workspace: Workspace | undefined,
  userFilters: CompanyFilter | undefined,
  filterTypesToCheck: Set<FilterConflictType>
): FilterConflict[] {
  if (!workspace || !userFilters) return [];

  const conflicts: FilterConflict[] = [];

  const hasGeographyConfig = isSamGeographyConfigured(
    workspace.configuration.sam.geography
  );
  const hasIndustryConfig = isSamIndustryConfigured(
    workspace.configuration.sam.industry
  );
  const hasProductConfig = isSomProductsConfigured(
    workspace.configuration.som.productIds,
    workspace.configuration.som.excludedProductIds
  );
  const hasRevenueConfig = isRangeDefined(
    workspace.configuration.som.revenueRange
  );
  const hasEmployeeConfig = isRangeDefined(
    workspace.configuration.som.employeeRange
  );

  // Check all specified filter types
  filterTypesToCheck.forEach((filterType) => {
    // First check if the market has configuration for this filter type
    if (!hasMarketConfig(workspace, filterType)) return;

    switch (filterType) {
      case "geography":
        if (
          hasGeographyConfig &&
          (userFilters.countryAlpha2s?.length ||
            userFilters.regionNames?.length ||
            userFilters.stateIds?.length ||
            userFilters.excludedCountryAlpha2s?.length)
        ) {
          detectGeographyConflicts(workspace, userFilters, conflicts);
        }
        break;

      case "industry":
        if (hasIndustryConfig && userFilters.industry) {
          detectIndustryConflicts(workspace, userFilters, conflicts);
        }
        break;

      case "revenue":
        if (hasRevenueConfig && userFilters.revenue) {
          detectRevenueConflicts(workspace, userFilters, conflicts);
        }
        break;

      case "employee":
        if (hasEmployeeConfig && userFilters.employees) {
          detectEmployeeConflicts(workspace, userFilters, conflicts);
        }
        break;

      case "products":
        if (hasProductConfig && userFilters.products) {
          detectProductConflicts(workspace, userFilters, conflicts);
        }
        break;
    }
  });

  return conflicts;
}

/**
 * Detect conflicts between user product filters and workspace product definitions
 */
function detectProductConflicts(
  workspace: Workspace,
  userFilters: CompanyFilter,
  conflicts: FilterConflict[]
): void {
  const somProductIds = workspace.configuration.som.productIds || [];
  const somExcludedProductIds =
    workspace.configuration.som.excludedProductIds || [];
  const { productsInclusionMethod } = workspace.configuration.som;

  // Get market definition strings
  const marketDefinitions: string[] = [];
  if (somProductIds.length > 0) {
    marketDefinitions.push(`Required products: ${somProductIds.join(", ")}`);
    if (productsInclusionMethod === "ALL_PRESENT") {
      marketDefinitions.push("All products must be present");
    }
  }
  if (somExcludedProductIds.length > 0) {
    marketDefinitions.push(
      `Excluded products: ${somExcludedProductIds.join(", ")}`
    );
  }

  const filterValues: string[] = [];

  // Extract included and excluded products from user filters
  const includedProductIds: number[] = [];
  const excludedProductIds: number[] = [];

  userFilters.products?.forEach((product) => {
    if (product.ids?.length) {
      if (product.inclusionMethod === "NONE_PRESENT") {
        excludedProductIds.push(...product.ids);
      } else {
        includedProductIds.push(...product.ids);
      }
    }
  });

  if (includedProductIds.length > 0) {
    filterValues.push(`Selected products: ${includedProductIds.join(", ")}`);
  }
  if (excludedProductIds.length > 0) {
    filterValues.push(`Excluded products: ${excludedProductIds.join(", ")}`);
  }

  if (filterValues.length > 0) {
    conflicts.push({
      type: "products",
      marketDefinition:
        marketDefinitions.length > 0
          ? marketDefinitions.join("\n")
          : "Products: none",
      filterValue: filterValues.join("\n"),
      isOverridden: false,
    });
  }
}

/**
 * Detect conflicts between user geography filters and workspace geography definitions
 */
function detectGeographyConflicts(
  workspace: Workspace,
  userFilters: CompanyFilter,
  conflicts: FilterConflict[]
): void {
  const samGeography = workspace.configuration.sam.geography;

  // Get market definition string
  const marketDefinitions: string[] = [];
  const includedMarketDefs: string[] = [];
  const excludedMarketDefs: string[] = [];

  // Include all geography types
  if (samGeography.countryIds?.length > 0) {
    includedMarketDefs.push(`Countries: ${samGeography.countryIds.join(", ")}`);
  }
  if (samGeography.regionsIds?.length > 0) {
    includedMarketDefs.push(`Regions: ${samGeography.regionsIds.join(", ")}`);
  }
  if (samGeography.stateIds?.length > 0) {
    includedMarketDefs.push(`States: ${samGeography.stateIds.join(", ")}`);
  }
  // Include all excluded geography types
  if (samGeography.excludedCountryIds?.length > 0) {
    excludedMarketDefs.push(
      `Countries: ${samGeography.excludedCountryIds.join(", ")}`
    );
  }
  // Add similar checks for excluded regions and states if they exist in the model

  // Add sections with proper formatting
  if (includedMarketDefs.length > 0) {
    marketDefinitions.push(
      `Included Geography: ${includedMarketDefs.join(", ")}`
    );
  }

  if (excludedMarketDefs.length > 0) {
    marketDefinitions.push(
      `Excluded Geography: ${excludedMarketDefs.join(", ")}`
    );
  }

  // Get user filter values
  const filterValues: string[] = [];
  const includedUserFilters: string[] = [];
  const excludedUserFilters: string[] = [];

  // Include all user geography types
  if ((userFilters.countryAlpha2s?.length ?? 0) > 0) {
    includedUserFilters.push(
      `Countries: ${userFilters.countryAlpha2s?.join(", ")}`
    );
  }
  if ((userFilters.regionNames?.length ?? 0) > 0) {
    includedUserFilters.push(`Regions: ${userFilters.regionNames?.join(", ")}`);
  }
  if ((userFilters.stateIds?.length ?? 0) > 0) {
    includedUserFilters.push(`States: ${userFilters.stateIds?.join(", ")}`);
  }

  // Include all excluded user geography types
  if ((userFilters.excludedCountryAlpha2s?.length ?? 0) > 0) {
    excludedUserFilters.push(
      `Countries: ${userFilters.excludedCountryAlpha2s?.join(", ")}`
    );
  }

  if (includedUserFilters.length > 0) {
    filterValues.push(`Included Geography: ${includedUserFilters.join(", ")}`);
  }
  if (excludedUserFilters.length > 0) {
    filterValues.push(`Excluded Geography: ${excludedUserFilters.join(", ")}`);
  }

  if (filterValues.length > 0) {
    conflicts.push({
      type: "geography",
      marketDefinition:
        marketDefinitions.length > 0
          ? marketDefinitions.join("\n")
          : "Geography: none",
      filterValue: filterValues.join("\n"),
      isOverridden: false,
    });
  }
}

/**
 * Detect conflicts between user industry filters and workspace industry definitions
 */

function detectIndustryConflicts(
  workspace: Workspace,
  userFilters: CompanyFilter,
  conflicts: FilterConflict[]
): void {
  const samIndustry = workspace.configuration.sam.industry;
  const userIndustry = userFilters.industry || {};

  // Get market definition string
  const marketDefinitions: string[] = [];

  // Group included market definitions
  const includedMarketDefs: string[] = [];
  if (samIndustry.sicIds?.length > 0) {
    includedMarketDefs.push(`SIC:- ${samIndustry.sicIds.join(", ")}`);
  }
  if (samIndustry.naicsIds?.length > 0) {
    includedMarketDefs.push(`NAICS:- ${samIndustry.naicsIds.join(", ")}`);
  }
  if (samIndustry.ids?.length > 0) {
    includedMarketDefs.push(`Industries:- ${samIndustry.ids.join(", ")}`);
  }

  // Group excluded market definitions
  const excludedMarketDefs: string[] = [];
  if (samIndustry.excludedSicIds?.length > 0) {
    excludedMarketDefs.push(`SIC:- ${samIndustry.excludedSicIds.join(", ")}`);
  }
  if (samIndustry.excludedNaicsIds?.length > 0) {
    excludedMarketDefs.push(
      `NAICS:- ${samIndustry.excludedNaicsIds.join(", ")}`
    );
  }
  if (samIndustry.excludedIds?.length > 0) {
    excludedMarketDefs.push(
      `Industries:- ${samIndustry.excludedIds.join(", ")}`
    );
  }

  // Add included and excluded sections separately
  if (includedMarketDefs.length > 0) {
    marketDefinitions.push(
      `Included Industries: ${includedMarketDefs.join(", ")}`
    );
  }

  if (excludedMarketDefs.length > 0) {
    marketDefinitions.push(
      `Excluded Industries: ${excludedMarketDefs.join(", ")}`
    );
  }

  // Get user filter values string
  const filterValues: string[] = [];

  // Group included user filters
  const includedUserFilters: string[] = [];
  if ((userIndustry.sicUS1987Ids?.length ?? 0) > 0) {
    includedUserFilters.push(`SIC:- ${userIndustry.sicUS1987Ids?.join(", ")}`);
  }
  if ((userIndustry.naics2012Ids?.length ?? 0) > 0) {
    includedUserFilters.push(
      `NAICS:- ${userIndustry.naics2012Ids?.join(", ")}`
    );
  }
  if ((userIndustry.industryIds?.length ?? 0) > 0) {
    includedUserFilters.push(
      `Industries:- ${userIndustry.industryIds?.join(", ")}`
    );
  }

  // Group excluded user filters
  const excludedUserFilters: string[] = [];
  if ((userIndustry.excludedSicUS1987Ids?.length ?? 0) > 0) {
    excludedUserFilters.push(
      `SIC:- ${userIndustry.excludedSicUS1987Ids?.join(", ")}`
    );
  }
  if ((userIndustry.excludedNaics2012Ids?.length ?? 0) > 0) {
    excludedUserFilters.push(
      `NAICS:- ${userIndustry.excludedNaics2012Ids?.join(", ")}`
    );
  }
  if ((userIndustry.excludedIndustryIds?.length ?? 0) > 0) {
    excludedUserFilters.push(
      `Industries:- ${userIndustry.excludedIndustryIds?.join(", ")}`
    );
  }

  // Add included and excluded sections separately
  if (includedUserFilters.length > 0) {
    filterValues.push(`Included Industries: ${includedUserFilters.join(", ")}`);
  }
  if (excludedUserFilters.length > 0) {
    filterValues.push(`Excluded Industries: ${excludedUserFilters.join(", ")}`);
  }

  // Always create conflict if user has any industry filters
  if (filterValues.length > 0) {
    conflicts.push({
      type: "industry",
      marketDefinition:
        marketDefinitions.length > 0
          ? marketDefinitions.join("\n")
          : "Industries: none",
      filterValue: filterValues.join("\n"),
      isOverridden: false,
    });
  }
}

function detectRevenueConflicts(
  workspace: Workspace,
  userFilters: CompanyFilter,
  conflicts: FilterConflict[]
): void {
  const somRevenue = workspace.configuration.som.revenueRange;

  if (somRevenue && userFilters.revenue) {
    const userRange = {
      min: userFilters.revenue.min,
      max: userFilters.revenue.max,
    };

    const somRange = {
      min: somRevenue.min,
      max: somRevenue.max,
    };

    // Always create conflict if user has any revenue filter
    conflicts.push(
      createConflict({
        type: "revenue",
        marketDefinition: `Revenue Range: ${buildRangeLabel(somRange)}`,
        filterValue: `Revenue Range: ${buildRangeLabel({
          min: userRange.min ?? undefined,
          max: userRange.max ?? undefined,
        })}`,
      })
    );
  }
}

function detectEmployeeConflicts(
  workspace: Workspace,
  userFilters: CompanyFilter,
  conflicts: FilterConflict[]
): void {
  const somEmployee = workspace.configuration.som.employeeRange;

  if (somEmployee && userFilters.employees) {
    const userRange = {
      min: userFilters.employees.min,
      max: userFilters.employees.max,
    };

    const somRange = {
      min: somEmployee.min,
      max: somEmployee.max,
    };

    // Always create conflict if user has any employee filter
    conflicts.push(
      createConflict({
        type: "employee",
        marketDefinition: `Employee Range: ${buildRangeLabel(somRange)}`,
        filterValue: `Employee Range: ${buildRangeLabel({
          min: userRange.min ?? undefined,
          max: userRange.max ?? undefined,
        })}`,
      })
    );
  }
}

export const hasFilterTypeChanged = (
  filterType: FilterConflictType,
  oldFilters: Workspace | undefined,
  newFilters: Workspace | undefined
): boolean => {
  if (!oldFilters && !newFilters) return false;
  if (!oldFilters && newFilters) return true;
  if (oldFilters && !newFilters) return true;

  switch (filterType) {
    case "geography":
      return !isEqual(
        {
          configGeography: oldFilters?.configuration?.sam?.geography,
        },
        {
          configGeography: newFilters?.configuration?.sam?.geography,
        }
      );

    case "industry":
      return !isEqual(
        {
          configIndustry: oldFilters?.configuration?.sam?.industry,
        },
        {
          configIndustry: newFilters?.configuration?.sam?.industry,
        }
      );

    case "revenue":
      return !isEqual(
        {
          configRevenue: oldFilters?.configuration?.som?.revenueRange,
        },
        {
          configRevenue: newFilters?.configuration?.som?.revenueRange,
        }
      );

    case "employee":
      return !isEqual(
        {
          configEmployees: oldFilters?.configuration?.som?.employeeRange,
        },
        {
          configEmployees: newFilters?.configuration?.som?.employeeRange,
        }
      );

    case "products":
      return !isEqual(
        {
          configProducts: {
            productIds: oldFilters?.configuration?.som?.productIds,
            excludedProductIds:
              oldFilters?.configuration?.som?.excludedProductIds,
          },
        },
        {
          configProducts: {
            productIds: newFilters?.configuration?.som?.productIds,
            excludedProductIds:
              newFilters?.configuration?.som?.excludedProductIds,
          },
        }
      );

    default:
      return false;
  }
};

export const getTopMostSelected = (
  ids: string[],
  itemsById: Map<string, { id: string; parentId?: string }>
): string[] => {
  const idSet = new Set(ids);
  return ids.filter((id) => {
    const item = itemsById.get(id);
    if (!item?.parentId) return true;
    return !idSet.has(item.parentId);
  });
};
